import { useState, useMemo, useCallback } from 'react';

export interface UsePaginationOptions {
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}

export interface UsePaginationReturn {
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  offset: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  resetPage: () => void;
}

/**
 * Hook for managing pagination state.
 * - `offset` is computed from page and pageSize
 * - `setPageSize` resets page to 1
 * - `resetPage` resets page to 1 (use when search/filters change)
 *
 * Total and totalPages are NOT tracked here — compute them from the query response
 * at the call site to avoid a redundant source of truth.
 */
export function usePagination(options: UsePaginationOptions = {}): UsePaginationReturn {
  const { defaultPageSize = 50, pageSizeOptions = [25, 50, 100] } = options;

  const [page, setPageRaw] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(defaultPageSize);

  const offset = useMemo(() => (page - 1) * pageSize, [page, pageSize]);

  const setPage = useCallback(
    (newPage: number) => {
      setPageRaw(Math.max(1, newPage));
    },
    []
  );

  const setPageSize = useCallback((newSize: number) => {
    setPageSizeRaw(newSize);
    setPageRaw(1);
  }, []);

  const resetPage = useCallback(() => {
    setPageRaw(1);
  }, []);

  return {
    page,
    pageSize,
    pageSizeOptions,
    offset,
    setPage,
    setPageSize,
    resetPage,
  };
}
